package hk.com.vr360_demo2.webapp.component

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.marzipano.AutoRotateOptions
import hk.com.vr360_demo2.webapp.component.marzipano.ImageUrlSourceOptions
import hk.com.vr360_demo2.webapp.component.marzipano.Level
import hk.com.vr360_demo2.webapp.component.marzipano.Marzipano
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaLinkHotspot
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaSceneConfig
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaViewParameters
import hk.com.vr360_demo2.webapp.component.marzipano.Scene
import hk.com.vr360_demo2.webapp.component.marzipano.SceneOptions
import hk.com.vr360_demo2.webapp.component.marzipano.Source
import hk.com.vr360_demo2.webapp.component.marzipano.SwitchOptions
import js.coroutines.awaitCancellation
import kotlin.js.js
import kotlin.js.unsafeCast
import kotlin.math.PI
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useRef
import web.cssom.Position
import web.cssom.pct
import web.cssom.px
import web.html.HTMLDivElement

/**
 * Embeds a single-scene Marzipano view without rendering link hotspots.
 * Accepts optional [linkHotspot] metadata for callers; it is never drawn.
 * Does not alter global `document` scroll styles (safe inside dialogs).
 */
external interface SingleHotspotScenePanoramaPreviewProps : Props {
    var scene: PanoramaSceneConfig
    var linkHotspot: PanoramaLinkHotspot?
    var autorotateEnabled: Boolean?
}

private fun clampIndex(index: Int, size: Int): Int {
    if (size <= 0) return 0
    return index.coerceIn(0, size - 1)
}

private fun sceneWithoutHotspots(scene: PanoramaSceneConfig): PanoramaSceneConfig =
    scene.copy(linkHotspots = emptyList())

private fun imageSourceOptions(baseUrl: String): ImageUrlSourceOptions {
    val options = js("{}")
    options.cubeMapPreviewUrl = "$baseUrl/preview.jpg"
    return options.unsafeCast<ImageUrlSourceOptions>()
}

private fun initialViewParams(viewParameters: PanoramaViewParameters?): dynamic {
    val params = js("{}")
    params.yaw = viewParameters?.yaw ?: 0.0
    params.pitch = viewParameters?.pitch ?: 0.0
    params.fov = viewParameters?.fov ?: 2.0
    return params
}

private fun sceneOptions(
    source: Source,
    geometry: Marzipano.CubeGeometry,
    view: Marzipano.RectilinearView,
): SceneOptions {
    val options = js("{}")
    options.source = source
    options.geometry = geometry
    options.view = view
    options.pinFirstLevel = true
    return options.unsafeCast<SceneOptions>()
}

private fun switchOptions(transitionDuration: Int): SwitchOptions {
    val options = js("{}")
    options.transitionDuration = transitionDuration
    return options.unsafeCast<SwitchOptions>()
}

private fun defaultLevels(): Array<Level> = arrayOf(
    level(tileSize = 256, size = 256, fallbackOnly = true),
    level(tileSize = 512, size = 512),
    level(tileSize = 512, size = 1024),
    level(tileSize = 512, size = 2048),
    level(tileSize = 512, size = 4096),
)

private fun level(tileSize: Int, size: Int, fallbackOnly: Boolean? = null): Level {
    val levelObj = js("{}")
    levelObj.tileSize = tileSize
    levelObj.size = size
    if (fallbackOnly != null) {
        levelObj.fallbackOnly = fallbackOnly
    }
    return levelObj.unsafeCast<Level>()
}

private fun autoRotateOptions(): AutoRotateOptions {
    val options = js("{}")
    options.yawSpeed = 0.05
    options.targetPitch = 0.0
    options.targetFov = PI / 2
    return options.unsafeCast<AutoRotateOptions>()
}

private data class AutoRotateControl(
    val start: () -> Unit,
    val stop: () -> Unit,
)

private val EmptyAutoRotateControl = AutoRotateControl(start = {}, stop = {})

val SingleHotspotScenePanoramaPreview = FC<SingleHotspotScenePanoramaPreviewProps> { props ->
    val viewerRef = useRef<HTMLDivElement>()
    val scenesRef = useRef<Array<Scene>>()
    val currentSceneIndexRef = useRef<Int>()
    val canAutoRotateRef = useRef<Boolean>()
    val autoRotateControlRef = useRef<AutoRotateControl>()
    val switchSceneRef = useRef<(Int, Boolean) -> Unit>()
    val resumeTimeoutRef = useRef<dynamic>()

    val sceneSig = "${props.scene.id}\u001e${props.scene.url}\u001e${props.scene.faceSize}\u001e" +
        "${props.scene.initialViewParameters?.yaw}\u001e${props.scene.initialViewParameters?.pitch}\u001e" +
        "${props.scene.initialViewParameters?.fov}"

    useEffect(dependencies = arrayOf(sceneSig, props.autorotateEnabled)) {
        val container = viewerRef.current ?: return@useEffect
        val displayScene = sceneWithoutHotspots(props.scene)

        container.asDynamic().innerHTML = ""

        val clearResumeTimeout: () -> Unit = {
            val timeout = resumeTimeoutRef.current
            if (timeout != null) {
                js("clearTimeout")(timeout)
                resumeTimeoutRef.current = null
            }
        }

        val scheduleAutoRotateResume: () -> Unit = {
            clearResumeTimeout()
            resumeTimeoutRef.current = js("setTimeout")({
                autoRotateControlRef.current?.start?.invoke()
                resumeTimeoutRef.current = null
            }, 3000)
        }

        val viewerOpts = js("{}")
        viewerOpts.stage = js("{}")
        viewerOpts.stage.preserveDrawingBuffer = true
        val viewer = Marzipano.Viewer(container, viewerOpts)

        val autorotate = Marzipano.autorotate(autoRotateOptions())
        canAutoRotateRef.current = props.autorotateEnabled != false

        fun stopAutoRotate() {
            viewer.stopMovement()
        }

        fun startAutoRotate() {
            if (canAutoRotateRef.current == true) {
                viewer.startMovement(autorotate)
            }
        }

        autoRotateControlRef.current = AutoRotateControl(start = ::startAutoRotate, stop = ::stopAutoRotate)

        switchSceneRef.current = switchScene@{ index: Int, _: Boolean ->
            val sceneList = scenesRef.current ?: return@switchScene
            if (sceneList.isEmpty()) return@switchScene
            val safeIndex = clampIndex(index, sceneList.size)
            sceneList[safeIndex].switchTo(switchOptions(300))
            currentSceneIndexRef.current = safeIndex
            autoRotateControlRef.current?.stop?.invoke()
            if (canAutoRotateRef.current == true) {
                autoRotateControlRef.current?.start?.invoke()
            }
        }

        val source = Marzipano.ImageUrlSource.fromString(
            "${displayScene.url}/{z}/{f}/{y}/{x}.jpg",
            imageSourceOptions(displayScene.url),
        )
        val geometry = Marzipano.CubeGeometry(displayScene.levels ?: defaultLevels())
        val limiter = Marzipano.RectilinearView.limit.traditional(displayScene.faceSize, (120 * PI) / 180)
        val view = Marzipano.RectilinearView(initialViewParams(displayScene.initialViewParameters), limiter)
        val scene = viewer.createScene(
            sceneOptions(source = source, geometry = geometry, view = view),
        )

        scenesRef.current = arrayOf(scene)
        switchSceneRef.current?.invoke(0, false)
        startAutoRotate()

        val handleActivity: () -> Unit = {
            autoRotateControlRef.current?.stop?.invoke()
            scheduleAutoRotateResume()
        }

        container.asDynamic().addEventListener("mousedown", handleActivity)
        container.asDynamic().addEventListener("mouseup", handleActivity)
        container.asDynamic().addEventListener("touchstart", handleActivity)
        container.asDynamic().addEventListener("touchmove", handleActivity)
        container.asDynamic().addEventListener("touchend", handleActivity)

        awaitCancellation {
            autoRotateControlRef.current?.stop?.invoke()
            clearResumeTimeout()
            container.asDynamic().removeEventListener("mousedown", handleActivity)
            container.asDynamic().removeEventListener("mouseup", handleActivity)
            container.asDynamic().removeEventListener("touchstart", handleActivity)
            container.asDynamic().removeEventListener("touchmove", handleActivity)
            container.asDynamic().removeEventListener("touchend", handleActivity)
            viewer.destroy()
            container.asDynamic().innerHTML = ""
            scenesRef.current = null
            switchSceneRef.current = null
            autoRotateControlRef.current = EmptyAutoRotateControl
            resumeTimeoutRef.current = null
        }
    }

    div {
        css {
            width = 100.pct
            height = 100.pct
            position = Position.relative
            minHeight = 220.px
            overflow = web.cssom.Overflow.hidden
        }
        ref = viewerRef
    }
}
